// src/features/apps/components/StoreReadinessCard.tsx

import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';
import { IconBuildingStore, IconShieldCheck } from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useHelpTokens } from '@/features/help/useHelpTokens';
import { FACTORY_STORE_INFO, type FactoryStoreKey } from '@/lib/constants/appFactory';
import type { AppCfgsDto } from '@/lib/types/App';
import type { FactoryStoreAccount } from '@/lib/types/AppFactory';
import type { AppLegalVersionSummary } from '@/lib/types/AppLegal';
import { PrivacyTemplate } from '../legal/PrivacyTemplate';
import { StoreAccountUtils } from '../StoreAccountUtils';
import type { WizardStep } from './AppRokuSetupWizard';

interface StoreReadinessCardProps {
  app: AppCfgsDto;
  /** The stores to chip; default = every store the app onboards. */
  stores?: FactoryStoreKey[];
  storeAccounts: FactoryStoreAccount[];
  legal: AppLegalVersionSummary | null;
  /** Absent = the reads failed (the card still renders what it can). */
  unavailable?: boolean;
  onOpenWizard: (step: WizardStep) => void;
}

/**
 * The Status tab's "Store readiness" card (B1): one chip per store for the
 * developer account behind the app, one for the privacy policy — each opening
 * its wizard step. Reads only; the daemon's ledger and the shard are the truth.
 */
export function StoreReadinessCard({
  app,
  stores = StoreAccountUtils.storesForApp(app),
  storeAccounts,
  legal,
  unavailable,
  onOpenWizard,
}: StoreReadinessCardProps) {
  const { selectedCId, selectedCustomer } = useAuth();
  const { operatorName } = useHelpTokens();
  const parentCIds = selectedCustomer?.cfg.parentCId ? [selectedCustomer.cfg.parentCId] : [];
  // A hand-set (or pre-generator) listing URL is a policy the customer hosts:
  // "Custom URL", never "Missing" (the listing is what the stores see).
  const privacy = PrivacyTemplate.readiness(
    app.listing?.privacyPolicyUrl,
    legal,
    PrivacyTemplate.derivedFacts(app),
  );

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Text fw={600}>Store readiness</Text>
        {unavailable && (
          <Text size="xs" c="dimmed">
            Store account status is currently unavailable; the chips below may be stale.
          </Text>
        )}
        {stores.map((store) => {
          const info = FACTORY_STORE_INFO[store];
          const chip = StoreAccountUtils.readinessChip(
            StoreAccountUtils.resolve(storeAccounts, selectedCId ?? 0, parentCIds, store),
            operatorName,
          );
          return (
            <Group key={store} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <IconBuildingStore size={16} />
                <Text size="sm">{info.label} developer account</Text>
                <Badge variant="light" color={chip.color} size="sm">
                  {chip.label}
                </Badge>
              </Group>
              <Button size="xs" variant="subtle" onClick={() => onOpenWizard('store')}>
                {chip.account ? 'Open' : 'Set up'}
              </Button>
            </Group>
          );
        })}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <IconShieldCheck size={16} />
            <Text size="sm">Privacy policy</Text>
            <Badge variant="light" color={privacy.color} size="sm">
              {privacy.label}
            </Badge>
          </Group>
          <Button size="xs" variant="subtle" onClick={() => onOpenWizard('privacy')}>
            {privacy.action}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
